<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

import { MapSource, MapLayer } from '@/components/map'
import { getBaseUrl } from '@/utils/url'

const store = useStore()

const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  mosaics: {
    type: Array, // Only quarterly array
    required: true
  },
  beforeId: {
    type: String,
    required: true
  },
  groupId: {
    type: String,
    required: true
  },
  layerId: {
    type: String,
    required: true
  },
  orgId: {
    type: Number,
    required: true
  },
  options: {
    type: Object,
    default: () => ({})
  }
})

const host = getBaseUrl()

const quarterNames = ['Q1', 'Q2', 'Q3', 'Q4']
const quarterlyAvailableQuartersByYear = ref({})
const quarterlyYearOptions = ref([])
const quarterlySelectedYear = ref(null)
const quarterlySelectedQuarter = ref(null)
const quarterlyLastLayedId = ref(null)

onMounted(() => {
  props.mosaics.forEach(m => {
    const date = new Date(m.first_acquired)
    const y = date.getUTCFullYear()
    // Quarter is 1..4
    const q = Math.floor(date.getUTCMonth() / 3) + 1

    if (!quarterlyAvailableQuartersByYear.value[y]) {
      quarterlyAvailableQuartersByYear.value[y] = new Set()
    }
    quarterlyAvailableQuartersByYear.value[y].add(q)
  })

  const numericYears = Object.keys(quarterlyAvailableQuartersByYear.value)
    .map(y => parseInt(y, 10))
    .sort((a, b) => a - b)

  quarterlyYearOptions.value = numericYears.map(y => String(y))

  // Default to the first mosaic
  if (props.mosaics.length) {
    const defaultMosaic = props.mosaics[0]
    const defaultDate = new Date(defaultMosaic.first_acquired)
    const defaultYear = defaultDate.getUTCFullYear()
    const defaultQuarter = Math.floor(defaultDate.getUTCMonth() / 3) + 1

    quarterlySelectedYear.value = String(defaultYear)
    quarterlySelectedQuarter.value = String(defaultQuarter)
    quarterlyLastLayedId.value = `planet-${defaultMosaic.name}-layer`
  }
})

const quarterOptionsForSelectedYear = computed(() => {
  if (!quarterlySelectedYear.value) return []
  const quartersSet =
    quarterlyAvailableQuartersByYear.value[Number(quarterlySelectedYear.value)]
  if (!quartersSet) return []
  return [...quartersSet].sort((a, b) => a - b).map(q => String(q))
})

const quarterlySelectedMosaic = computed(() => {
  const selYear = Number(quarterlySelectedYear.value)
  const selQuarter = Number(quarterlySelectedQuarter.value)

  return props.mosaics.find(m => {
    const date = new Date(m.first_acquired)
    const q = Math.floor(date.getUTCMonth() / 3) + 1
    return date.getUTCFullYear() === selYear && q === selQuarter
  }) || props.mosaics[0]
})

watch([quarterlySelectedYear, quarterlySelectedQuarter], () => {
  if (quarterlySelectedMosaic.value) {
    quarterlyLastLayedId.value = `planet-${quarterlySelectedMosaic.value.name}-layer`
  }
})
</script>

<template>
  <div v-if="mosaics.length">
    <div class="pointer-events-auto">
      <div class="flex space-x-1 -mr-1">
        <Select
          v-if="quarterlyYearOptions.length"
          v-model="quarterlySelectedYear"
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a year" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem
                v-for="(year, index) in quarterlyYearOptions"
                :key="index"
                :value="year"
              >
                {{ year }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>

        <Select
          v-if="quarterOptionsForSelectedYear.length"
          v-model="quarterlySelectedQuarter"
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a quarter" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem
                v-for="(q, index) in quarterOptionsForSelectedYear"
                :key="index"
                :value="q"
              >
                {{ quarterNames[Number(q) - 1] }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div v-if="map">
      <div v-for="mosaic in mosaics" :key="mosaic.name">
        <MapSource
          v-if="quarterlyLastLayedId === `planet-${mosaic.name}-layer`"
          :map="map"
          type="raster"
          :id="`${layerId}-planet-${mosaic.name}-source`"
          :options="{
            tiles: [`${host}/tulum/tiles/planetscope/${orgId}/${mosaic.name}/{z}/{x}/{y}.png`],
            tileSize: 256
          }"
        >
          <MapLayer
            :map="map"
            :id="`${layerId}-planet-${mosaic.name}-layer`"
            type="raster"
            :source="`${layerId}-planet-${mosaic.name}-source`"
            :before-id="beforeId"
            :group-id="groupId"
            :options="options"
          />
        </MapSource>
      </div>
    </div>
  </div>
</template>
